import React, { useState } from 'react';
import { Search, Filter, Calendar as CalendarIcon, Clock, MapPin, User } from 'lucide-react';
import { useAppointments } from '../../hooks/useAppointments';
import { Appointment } from '../../types';

export const Appointments: React.FC = () => {
  const { appointments, loading } = useAppointments();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'risk' | 'department'>('date');

  const departments = [...new Set(appointments.map(apt => apt.department))];

  const filteredAppointments = appointments
    .filter(apt => {
      const matchesSearch = apt.patientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           apt.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           apt.visitType.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = !filterDepartment || apt.department === filterDepartment;
      const matchesRisk = !filterRisk || apt.riskLevel === filterRisk;
      
      return matchesSearch && matchesDepartment && matchesRisk;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime();
        case 'risk':
          return b.noShowProbability - a.noShowProbability;
        case 'department':
          return a.department.localeCompare(b.department);
        default:
          return 0;
      }
    });

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'no-show': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Appointments</h1>
        <p className="text-gray-600 mt-2">Manage and monitor all appointments</p>
      </div>

      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search appointments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
              
              <select
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Risk Levels</option>
                <option value="low">Low Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="high">High Risk</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'risk' | 'department')}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date">Date</option>
                <option value="risk">Risk Level</option>
                <option value="department">Department</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-4 font-medium text-gray-700">Patient</th>
                <th className="text-left p-4 font-medium text-gray-700">Date & Time</th>
                <th className="text-left p-4 font-medium text-gray-700">Department</th>
                <th className="text-left p-4 font-medium text-gray-700">Visit Type</th>
                <th className="text-left p-4 font-medium text-gray-700">Risk Level</th>
                <th className="text-left p-4 font-medium text-gray-700">Status</th>
                <th className="text-left p-4 font-medium text-gray-700">Reminders</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAppointments.map((appointment) => (
                <tr key={appointment.id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{appointment.patientId}</p>
                        <p className="text-sm text-gray-600">{appointment.patientAgeGroup}</p>
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{appointment.appointmentDate}</p>
                        <p className="text-sm text-gray-600 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {appointment.appointmentTime}
                        </p>
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <p className="font-medium text-gray-900">{appointment.department}</p>
                    <p className="text-sm text-gray-600 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {appointment.location}
                    </p>
                  </td>
                  
                  <td className="p-4">
                    <p className="text-gray-900">{appointment.visitType}</p>
                  </td>
                  
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRiskColor(appointment.riskLevel)}`}>
                      {appointment.riskLevel} ({appointment.noShowProbability}%)
                    </span>
                  </td>
                  
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                      {appointment.status}
                    </span>
                  </td>
                  
                  <td className="p-4">
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">{appointment.remindersSent} sent</p>
                      {appointment.lastReminderSent && (
                        <p className="text-gray-600">
                          Last: {new Date(appointment.lastReminderSent).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            Showing {filteredAppointments.length} of {appointments.length} appointments
          </p>
        </div>
      </div>
    </div>
  );
};